/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'motion/react';
import { RiGlobalLine } from 'react-icons/ri';
import { IoLayersOutline, IoHelp } from 'react-icons/io5';

import { TbFilterCog } from 'react-icons/tb';
import { VscSymbolInterface } from 'react-icons/vsc';

import { IoIosLogOut } from 'react-icons/io';
import { GoGear } from 'react-icons/go';
import { HiOutlinePencil } from 'react-icons/hi2';
import { AiOutlineTeam } from 'react-icons/ai';
import useAsyncEffect from 'use-async-effect';
import { LiaBrainSolid } from 'react-icons/lia';
import { isAdmin } from '@/utils/rbac-utils';
import { Pages } from '@/webapi/pages';
import {
  AccountContext,
  AccountContextProps,
  useFeatureBit,
} from '@/features/account-context';

import { FeatureBit } from '@/webapi/use-auth-api';
import { isBrowser, nav } from '@/utils/browser';
import { VSpace } from '@/components/spacing';
import {
  OverlayType,
  showOverlay,
} from '@/features/common-overlays/overlay-manager';
import { Flexbox } from '@/components/flex';
import { routes } from '@/webapi/routes';
import { RecentStoresList } from '@/components/recent-stores-list';

export const HeaderPopover: FC<{
  onLogout: () => void;
  disabledAccount?: boolean;
  children?: React.ReactNode;
  onPopoverOpenChange?: (isOpen: boolean) => void;
}> = ({ children, onLogout, disabledAccount, onPopoverOpenChange }) => {
  const clickMeButtonRef = useRef<HTMLButtonElement | undefined>();
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isPopoverMounted, setIsPopoverMounted] = useState(false);
  const ctx = useContext(AccountContext);
  const store = ctx?.account?.store;
  const isAppEmbedRevertEnabled = useFeatureBit(FeatureBit.APP_EMBED_REVERT);
  const isAiSettingsEnabled = useFeatureBit(FeatureBit.AI_SETTINGS_PAGE, false);

  const setPopoverOpen = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        setIsPopoverMounted(true);
      }
      setIsPopoverOpen(isOpen);
      onPopoverOpenChange?.(isOpen);
    },
    [onPopoverOpenChange],
  );

  const onExitAnimationComplete = useCallback(() => {
    setIsPopoverMounted(false);
  }, []);

  useEffect(() => {
    if (disabledAccount && isPopoverOpen) {
      setPopoverOpen(false);
    }
  }, [disabledAccount, isPopoverOpen, setPopoverOpen]);

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  const cancelScheduledClose = useCallback(() => {
    clearTimeout(closeTimeoutRef.current);
  }, []);

  const openPopoverOnHover = useCallback(() => {
    if (disabledAccount) {
      return;
    }
    cancelScheduledClose();
    setPopoverOpen(true);
  }, [cancelScheduledClose, disabledAccount, setPopoverOpen]);

  const scheduleClosePopoverOnHover = useCallback(() => {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(() => setPopoverOpen(false), 200);
  }, [cancelScheduledClose, setPopoverOpen]);

  const onReferralClick = () => {
    showOverlay(OverlayType.REFER_A_FRIEND);
  };

  const showStrategyPage = useFeatureBit(FeatureBit.SHOW_STRATEGY_PAGE);

  const [accountsCount, setAccountsCount] = useState<number | null>(null);

  useAsyncEffect(async () => {
    if (isBrowser()) {
      const resp = await fetch(routes.listAccounts(), {
        method: `POST`,
        headers: {
          'Content-Type': `application/json`,
        },
        body: JSON.stringify({
          page: 0,
          limit: 1,
          searchText: ``,
        }),
      });
      if (resp.status === 200) {
        const jsResp = await resp.json();
        setAccountsCount(jsResp?.total);
      }
    }
  }, []);

  return (
    <Popover
      containerStyle={{ zIndex: `25` }}
      isOpen={disabledAccount ? false : isPopoverMounted}
      positions={[`bottom`]}
      padding={10}
      ref={clickMeButtonRef}
      content={({ position, childRect, popoverRect }) => (
        <ArrowWrapper
          onMouseEnter={openPopoverOnHover}
          onMouseLeave={scheduleClosePopoverOnHover}
        >
          <AnimatePresence onExitComplete={onExitAnimationComplete}>
            {isPopoverOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: `easeOut` }}
              >
                <ArrowContainer
                  position={position}
                  childRect={childRect}
                  popoverRect={popoverRect}
                  arrowColor="white"
                  arrowSize={10}
                  className="popover-arrow-container"
                  arrowClassName="popover-arrow"
                >
                  <Wrapper>
                    <UpperPart>
                      <UserName>
                        {capitalize(store?.firstName)}
                        {` `}
                        {capitalize(store?.lastName)}
                        <span
                          className="edit-btn"
                          onClick={() => nav(Pages.ACCOUNT_SETTINGS)}
                        >
                          <HiOutlinePencil size={15} />
                        </span>
                      </UserName>
                      <VSpace value={0.01} />
                      <Email>{store?.email}</Email>
                      <VSpace value={0.01} />
                      <MenuButton
                        className="small"
                        style={{ gridTemplateColumns: `1fr` }}
                        onClick={() => nav(Pages.ACCOUNTS)}
                      >
                        <Flexbox
                          direction="row"
                          justify="space-between"
                          align="center"
                          gap={1}
                        >
                          <span>
                            Connected Stores
                            {accountsCount > 0 &&
                              ` (${Number(accountsCount).toLocaleString(
                                `en-US`,
                              )})`}
                            {` `}›
                          </span>
                          <MenuButton className="small" onClick={onLogout}>
                            <span>Logout</span>
                            <IoIosLogOut size={14} />
                          </MenuButton>
                        </Flexbox>
                      </MenuButton>
                      <RecentStoresList />
                    </UpperPart>
                    <BottomPart>
                      <BottomButtonsWrapper>
                        <StoreDomain>{takeDomain(ctx)}</StoreDomain>
                        <MenuButton onClick={() => nav(Pages.SHOP_SETTINGS)}>
                          <GoGear size={16} />
                          <span>Account Settings</span>
                        </MenuButton>
                        {isAdmin(ctx?.account?.store?.role) && (
                          <MenuButton onClick={() => nav(Pages.TEAMMATES)}>
                            <AiOutlineTeam size={16} />
                            <span>My Teammates</span>
                          </MenuButton>
                        )}
                        {isAiSettingsEnabled && (
                          <MenuButton onClick={() => nav(Pages.AISETTINGS)}>
                            <LiaBrainSolid size={16} />
                            <span>AI Settings</span>
                          </MenuButton>
                        )}
                        <MenuButton onClick={() => nav(Pages.GLOBAL_RULES)}>
                          <RiGlobalLine size={16} />
                          <span>Global Rules</span>
                        </MenuButton>

                        <MenuButton onClick={() => nav(Pages.INTEGRATIONS)}>
                          <VscSymbolInterface size={16} />
                          <span>Integrations</span>
                        </MenuButton>
                        {showStrategyPage && (
                          <MenuButton onClick={() => nav(Pages.STRATEGIES)}>
                            <TbFilterCog size={16} />
                            <span>Strategies</span>
                          </MenuButton>
                        )}
                        {isAppEmbedRevertEnabled && (
                          <MenuButton onClick={() => nav(Pages.THEMES)}>
                            <IoLayersOutline size={16} />
                            <span>Theme Settings</span>
                          </MenuButton>
                        )}
                        <MenuButton
                          onClick={() =>
                            window.open(`https://help.visually.io`, `support`)
                          }
                        >
                          <IoHelp size={16} />
                          <span>Help Center</span>
                        </MenuButton>
                      </BottomButtonsWrapper>
                      <ShareButton onClick={onReferralClick}>
                        Share the ❤️ and earn up to $1500 ›
                      </ShareButton>
                    </BottomPart>
                  </Wrapper>
                </ArrowContainer>
              </motion.div>
            )}
          </AnimatePresence>
        </ArrowWrapper>
      )}
    >
      <button
        data-testid="popover-btn"
        type="button"
        onMouseEnter={openPopoverOnHover}
        onMouseLeave={scheduleClosePopoverOnHover}
      >
        {children}
      </button>
    </Popover>
  );
};

const ArrowWrapper = styled.div`
  filter: drop-shadow(0 0.6rem 0.7rem rgba(151, 210, 206, 0.22))
    drop-shadow(0 1px 1px rgba(0, 0, 0, 0.15));

  .popover-arrow {
    z-index: 1;
  }
`;

const Wrapper = styled.div`
  position: relative;
  z-index: 2;

  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: white;
  font-family: Eesti, serif;

  min-width: 33rem;
  max-width: 33rem;
  margin-left: -28rem;

  border-radius: 1.8rem;

  user-select: none;

  && * {
    user-select: none;
  }
`;

const UpperPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  padding: 2rem 3rem;

  background-color: white;
`;

const UserName = styled.span`
  font-size: 1.8rem;
  font-weight: 600;

  .edit-btn {
    position: relative;
    margin-left: 0.5rem;
    cursor: pointer;

    svg {
      position: absolute;
      top: 0.3rem;
    }

    &:hover {
      opacity: 0.8;
    }
  }
`;

const Email = styled.span`
  color: black;
  font-weight: 300;
  font-size: 1.2rem;
`;

const BottomButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0 2rem;
`;

const MenuButton = styled.button`
  display: grid;
  grid-template-columns: 2.5rem 1fr;
  align-items: center;
  justify-content: flex-start;
  text-align: start;
  gap: 1rem;
  font-family: Eesti, sans-serif;
  color: #414141;

  padding: 0;
  margin: 0;

  font-size: 1.4rem;

  &&.small {
    grid-template-columns: 1fr auto;
    color: black;
    font-weight: 300;
    font-size: 1.2rem;
    gap: 1rem;
  }

  && > span:hover {
    text-decoration: underline;
  }

  transition: opacity 0.2s ease-out;
  cursor: pointer;

  &&:hover {
    opacity: 0.7;
  }

  &&:active {
    && > span {
      text-decoration: none;
    }
  }
`;

const BottomPart = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  padding: 2rem 1rem 1rem 1rem;
  background-color: rgb(244 246 249 / 0.8);
`;

const StoreDomain = styled.span`
  font-size: 1.3rem;
  color: #a6afb8;
`;

const ShareButton = styled.button`
  outline: none;
  border: none;
  font-size: 1.3rem;
  cursor: pointer;
  background: #ffe2f2;
  box-shadow: 0 2px 4px 0 rgba(255, 94, 165, 0.07);
  color: #414141;
  border-radius: 1.2rem;
  padding: 1.2rem 0;
  font-family: Eesti, sans-serif;

  &&:hover {
    text-decoration: underline;
  }
`;

function takeDomain(ctx: AccountContextProps) {
  return ctx.account?.store?.domain
    ?.replace(`https://`, ``)
    ?.replace(`www.`, ``);
}

function capitalize(str: string) {
  if (str) {
    return str[0].toUpperCase() + str.slice(1);
  }
  return ``;
}
